import { bookingInquiryStrings } from '@data/bookingInquiry';
import {
  TradeCondition,
  BookingInquirySource,
  BookingInquiryStatus,
  BookingInquiryUseTradingAgency,
} from '@models/next/BookingInquiryEnums';
import { Company } from '@models/next/Company';
import { BookingInquiryContact } from '@models/next/Contact';
import { Country } from '@models/next/Country';
import { PaginationType } from '@models/next/PaginationType';
import { RoutePlan } from '@models/next/RoutePlan';
import { AdminUser } from '@models/next/User';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const BookingInquiryCargoLoose = z.discriminatedUnion('tag', [
  z.object({
    tag: z.literal('LWH'),
    name: z.string(),
    num: z.number().optional(),
    length: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    weight: z.number().optional(),
  }),
  z.object({
    tag: z.literal('WV'),
    name: z.string(),
    num: z.number().optional(),
    total_volume: z.number().optional(),
    total_weight: z.number().optional(),
  }),
]);

export type BookingInquiryCargoLoose = z.infer<typeof BookingInquiryCargoLoose>;

export const BookingInquiryCargosByPort = z
  .object({
    port_name: z.string(),
    cargos: BookingInquiryCargoLoose.array(),
  })
  .array();

export type BookingInquiryCargosByPort = z.infer<typeof BookingInquiryCargosByPort>;

export const BookingInquiryCargosByPickupAddress = z
  .object({
    pickup_address: BookingInquiryContact,
    cargos: BookingInquiryCargoLoose.array(),
  })
  .array();

export type BookingInquiryCargosByPickupAddress = z.infer<
  typeof BookingInquiryCargosByPickupAddress
>;

export const BookingInquiryCargos = z
  .object({
    cargos_by_port: BookingInquiryCargosByPort,
    cargos_by_pickup_address: BookingInquiryCargosByPickupAddress,
  })
  .partial();

export type BookingInquiryCargos = z.infer<typeof BookingInquiryCargos>;

export const BookingInquiryRoutePlan = z.object({
  pol: z.string(),
  pod: z.string(),
  has_pickups: z.boolean(),
  pickup_date: f.dateString().nullish(),
  move_in_date: f.dateString().nullish(),
  warehouse_date: f.dateString(),
  pol_date: z.string(),
  pod_date: z.string(),
  delivery_date: f.dateString(),
  receiver_city: z.string(),
  receiving_city: z.string(),
});

export type BookingInquiryRoutePlan = z.infer<typeof BookingInquiryRoutePlan>;

export const BookingInquiryFields = z.object({
  agency_name: z.string().nullish(),
  cargos: BookingInquiryCargos,
  company_name: z.string().nullish().label(bookingInquiryStrings.companyName),
  consignee: BookingInquiryContact.nullish(),
  created_at: z.string().label(bookingInquiryStrings.ReceiptionTime),
  customer_no: z.string().label(bookingInquiryStrings.customerNo),
  d2d_no: z.string().label(bookingInquiryStrings.d2dNo),
  english_name: z.string(),
  has_pickups: z.boolean(),
  id: z.number(),
  member_no: z.string().nullish().label(bookingInquiryStrings.memberNo),
  member_salesperson: z.string().nullish(),
  move_in_date: f.dateString().nullish(),
  owner: AdminUser.nullish(),
  owner_company: Company.nullish(),
  pickup_date: f.dateString().nullish(),
  preferred_arrival_date: z.string(),
  receiver: BookingInquiryContact.nullish(),
  salesperson_china_last_name: z.string().nullish(),
  salesperson_china_name: z.string().nullish(),
  salesperson_japan_last_name: z.string().nullish(),
  salesperson_japan_name: z.string().nullish(),
  salesperson_japan: AdminUser.nullish(),
  salesperson_china: AdminUser.nullish(),
  salesperson_customs: AdminUser.nullish(),
  shipper: BookingInquiryContact.nullish(),
  source: BookingInquirySource,
  special_note: z.string(),
  status: z.string(),
  trade_condition: TradeCondition,
  trading_agency_payer: Country,
  use_rcep: z.boolean().nullish(),
  use_trading_agency: BookingInquiryUseTradingAgency,
  user: AdminUser,
  route_plan: RoutePlan.nullish(),
});

export type BookingInquiryFields = z.infer<typeof BookingInquiryFields>;

export const DashboardBookingInquiry = BookingInquiryFields.pick({
  agency_name: true,
  company_name: true,
  created_at: true,
  d2d_no: true,
  english_name: true,
  id: true,
  member_no: true,
  member_salesperson: true,
  owner: true,
  owner_company: true,
  result: true,
  salesperson_china_last_name: true,
  salesperson_china_name: true,
  salesperson_japan_last_name: true,
  salesperson_japan_name: true,
  source: true,
  status: true,
  type: true,
  user: true,
}).merge(
  z.object({
    consignee: z.string().nullish(),
  }),
);

export type DashboardBookingInquiry = z.infer<typeof DashboardBookingInquiry>;

export const DashboardBookingInquiryQuery = z
  .object({
    agency_id: z.number(),
    company_name: BookingInquiryFields.shape.company_name,
    created_at_after: f.dateString().label(bookingInquiryStrings.ReceiptionTime),
    created_at_before: f.dateString().label(bookingInquiryStrings.ReceiptionTime),
    d2d_no: BookingInquiryFields.shape.d2d_no,
    email: z.string().label(bookingInquiryStrings.email),
    fullname: z.string().label(bookingInquiryStrings.customerPrincipal),
    member_no: BookingInquiryFields.shape.member_no,
    member_salesperson_id: z.number(),
    ordering: z.string(),
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    salesperson_china_id: z.number(),
    salesperson_japan_id: z.number(),
    status: BookingInquiryStatus.array(),
  })
  .partial();

export type DashboardBookingInquiryQuery = z.infer<typeof DashboardBookingInquiryQuery>;

export const DashboardBookingInquiryPages = z.object({
  count: z.number(),
  results: DashboardBookingInquiry.array(),
});

export type DashboardBookingInquiryPages = z.infer<typeof DashboardBookingInquiryPages>;
